import { Graphics } from 'pixi.js';
import { drawIsoBox, drawIsoBoxXY, drawIsoCylinder, drawIsoRect3D } from './IsoShapes';
import * as C from './colors';

export const DESK_LAYOUT = {
  seatOffsetY: -0.55,
  chairWorldOffsetY: -0.35,
  deskWorldOffsetY: 0.2,
} as const;

// Helper: convert a world-Y offset to screen position relative to tile center
function worldYToScreen(cx: number, cy: number, wy: number): { sx: number; sy: number } {
  return { sx: cx + wy * -32, sy: cy + wy * 16 };
}

/** Desk + Chair. */
export function drawDesk(g: Graphics, cx: number, cy: number): void {
  const chair = worldYToScreen(cx, cy, DESK_LAYOUT.chairWorldOffsetY);
  const desk = worldYToScreen(cx, cy, DESK_LAYOUT.deskWorldOffsetY);

  // Chair
  g.ellipse(chair.sx, chair.sy + 1, 3.5, 1.8);
  g.fill({ color: 0x303030 });
  drawIsoBox(g, chair.sx, chair.sy, 0.02, 2, 1, C.METAL_GREY);
  drawIsoBox(g, chair.sx, chair.sy, 0.11, 2, 3, C.FABRIC_BLUE);
  drawIsoBoxXY(g, chair.sx, chair.sy - 1, 0.11, 0.03, 4, 5, C.FABRIC_BLUE);

  // Desk legs first (drawn under surface)
  const legH = 7;
  const surfH = 2;
  const deskW = 0.8;
  const deskD = 0.3;
  const halfW = deskW / 2;
  const halfD = deskD / 2;
  for (const lp of [{ wx: -halfW, wy: -halfD }, { wx: halfW, wy: -halfD }, { wx: halfW, wy: halfD }, { wx: -halfW, wy: halfD }]) {
    drawIsoBox(g, desk.sx + lp.wx * 32 + lp.wy * -32, desk.sy + lp.wx * 16 + lp.wy * 16, 0.03, legH, 0, C.WOOD_DARK);
  }

  // Desk surface
  drawIsoRect3D(g, desk.sx, desk.sy, deskW, deskD, surfH, legH, C.WOOD_LIGHT);
  const onDesk = legH + surfH;

  // Monitor
  drawIsoRect3D(g, desk.sx, desk.sy, 0.05, 0.03, 1, onDesk, C.METAL_GREY);
  drawIsoRect3D(g, desk.sx, desk.sy, 0.02, 0.02, 4, onDesk + 1, C.METAL_GREY);
  drawIsoRect3D(g, desk.sx, desk.sy, 0.5, 0.05, 4, onDesk + 5, C.PLASTIC_BLACK);
  drawIsoRect3D(g, desk.sx, desk.sy, 0.45, 0.02, 3, onDesk + 6, C.SCREEN_ON);

  // Keyboard + mouse
  drawIsoRect3D(g, desk.sx, desk.sy, 0.25, 0.1, 0.5, onDesk, C.PLASTIC_WHITE);
  drawIsoRect3D(g, desk.sx + 7, desk.sy + 1, 0.05, 0.04, 0.5, onDesk, C.PLASTIC_WHITE);
}

/** Coffee machine. */
export function drawCoffeeMachine(g: Graphics, cx: number, cy: number): void {
  const counterH = 8;
  drawIsoRect3D(g, cx, cy, 0.6, 0.5, counterH, 0, C.METAL_GREY);

  // Machine body — offset toward back of counter so it's visible
  const back = worldYToScreen(cx, cy, -0.1);
  drawIsoRect3D(g, back.sx, back.sy, 0.3, 0.25, 13, counterH, C.METAL_DARK);

  // Cup in front
  const front = worldYToScreen(cx, cy, 0.12);
  drawIsoCylinder(g, front.sx, front.sy, 0.05, 3, counterH, C.CERAMIC_WHITE);

  // Steam
  g.circle(front.sx, front.sy - counterH - 5, 1.2);
  g.fill({ color: 0xffffff, alpha: 0.3 });
}

/** Small potted plant. */
export function drawPlantSmall(g: Graphics, cx: number, cy: number): void {
  drawIsoCylinder(g, cx, cy, 0.14, 7, 0, C.POT_BROWN);
  const base = cy - 8;
  for (const l of [
    { x: -3, y: -2, r: 3.5 }, { x: 3, y: -2, r: 3.5 },
    { x: 0, y: -5, r: 4 }, { x: -2, y: -8, r: 3 },
    { x: 2, y: -7, r: 3 }, { x: 0, y: -10, r: 2.5 },
  ]) {
    g.circle(cx + l.x, base + l.y, l.r);
    g.fill({ color: l.y < -5 ? C.PLANT_GREEN.highlight : C.PLANT_GREEN.base });
  }
}

/** Large plant. */
export function drawPlantLarge(g: Graphics, cx: number, cy: number): void {
  drawIsoCylinder(g, cx, cy, 0.16, 9, 0, C.POT_BROWN);
  drawIsoRect3D(g, cx, cy, 0.05, 0.05, 12, 9, C.WOOD_DARK);
  const base = cy - 22;
  for (const l of [
    { x: -5, y: 3, r: 5 }, { x: 5, y: 3, r: 5 },
    { x: 0, y: 0, r: 6 }, { x: -3, y: -4, r: 5 },
    { x: 3, y: -3, r: 5 }, { x: 0, y: -7, r: 4 },
  ]) {
    g.circle(cx + l.x, base + l.y, l.r);
    g.fill({ color: l.y < 0 ? C.PLANT_GREEN.highlight : C.PLANT_DARK.base });
  }
}

/** Meeting table — rectangular, wider than a desk. */
export function drawMeetingTable(g: Graphics, cx: number, cy: number): void {
  const legH = 7;
  const tableW = 0.9;
  const tableD = 0.6;
  const halfW = tableW / 2;
  const halfD = tableD / 2;

  // Legs
  for (const lp of [{ wx: -halfW, wy: -halfD }, { wx: halfW, wy: -halfD }, { wx: halfW, wy: halfD }, { wx: -halfW, wy: halfD }]) {
    drawIsoBox(g, cx + lp.wx * 32 + lp.wy * -32, cy + lp.wx * 16 + lp.wy * 16, 0.03, legH, 0, C.METAL_GREY);
  }

  // Surface
  drawIsoRect3D(g, cx, cy, tableW, tableD, 2, legH, C.WOOD_RED);

  // Papers
  drawIsoRect3D(g, cx - 3, cy, 0.12, 0.08, 0.3, legH + 2, C.PLASTIC_WHITE);
  drawIsoRect3D(g, cx + 3, cy + 1, 0.1, 0.06, 0.3, legH + 2, {
    highlight: 0xf0e8d0, base: 0xe0d8c0, shadow: 0xc8c0a8, dark: 0xb0a890,
  });
}

/** Server rack — tall rectangular cabinet. */
export function drawServerRack(g: Graphics, cx: number, cy: number): void {
  drawIsoRect3D(g, cx, cy, 0.4, 0.35, 26, 0, C.METAL_DARK);

  // Rack unit panels on the FRONT face (offset forward in world-Y by half-depth)
  const front = worldYToScreen(cx, cy, 0.17); // half of 0.35 depth
  for (let i = 0; i < 5; i++) {
    drawIsoRect3D(g, front.sx, front.sy, 0.35, 0.02, 3, 3 + i * 5, C.PLASTIC_BLACK);
    // LED on front face
    g.circle(front.sx - 8, front.sy - 5 - i * 5, 1);
    g.fill({ color: i < 3 ? 0x40ff40 : 0x404040 });
  }
}

/** Door — tall upright panel at the back edge of the tile. */
export function drawDoor(g: Graphics, cx: number, cy: number): void {
  // Push door to back edge of tile (world-Y = -0.4)
  const door = worldYToScreen(cx, cy, -0.4);

  drawIsoRect3D(g, door.sx, door.sy, 0.7, 0.08, 22, 0, C.WOOD_DARK);
  drawIsoRect3D(g, door.sx, door.sy, 0.6, 0.04, 20, 1, {
    highlight: 0x7a9a6a, base: 0x5a7a4a, shadow: 0x3a5a2a, dark: 0x2a4a1a,
  });
  g.circle(door.sx + 8, door.sy - 10, 1.5);
  g.fill({ color: C.METAL_GREY.highlight });

  // Floor mat in front of door
  drawIsoRect3D(g, cx, cy, 0.5, 0.2, 0.3, 0, {
    highlight: 0x5a4a3a, base: 0x4a3a2a, shadow: 0x3a2a1a, dark: 0x2a1a0a,
  });
}

/** Water cooler — rectangular base + cylinder bottle. */
export function drawWaterCooler(g: Graphics, cx: number, cy: number): void {
  drawIsoRect3D(g, cx, cy, 0.25, 0.25, 12, 0, C.PLASTIC_WHITE);
  drawIsoCylinder(g, cx, cy, 0.1, 10, 12, {
    highlight: 0x88bbee, base: 0x5599cc, shadow: 0x3377aa, dark: 0x225588,
  });
}

/** Vending machine — tall rectangular cabinet. */
export function drawVendingMachine(g: Graphics, cx: number, cy: number): void {
  drawIsoRect3D(g, cx, cy, 0.5, 0.35, 26, 0, C.METAL_GREY);

  // Glass panel and shelves on the FRONT face
  const front = worldYToScreen(cx, cy, 0.17); // half of 0.35 depth

  drawIsoRect3D(g, front.sx, front.sy, 0.42, 0.02, 18, 4, {
    highlight: 0x304050, base: 0x203040, shadow: 0x182030, dark: 0x101820,
  });

  const colors = [
    { highlight: 0xee5555, base: 0xcc3333, shadow: 0xaa2222, dark: 0x881111 },
    { highlight: 0x55ee55, base: 0x33cc33, shadow: 0x22aa22, dark: 0x118811 },
    { highlight: 0x5555ee, base: 0x3333cc, shadow: 0x2222aa, dark: 0x111188 },
    { highlight: 0xeeee55, base: 0xcccc33, shadow: 0xaaaa22, dark: 0x888811 },
  ];
  for (let i = 0; i < 4; i++) {
    drawIsoRect3D(g, front.sx, front.sy, 0.36, 0.01, 3, 6 + i * 4.5, colors[i]!);
  }
}

/** Whiteboard — rectangular board on legs. */
export function drawWhiteboard(g: Graphics, cx: number, cy: number): void {
  // Legs at left and right ends of the board (world X = ±0.35)
  const legLx = cx + (-0.35) * 32;
  const legLy = cy + (-0.35) * 16;
  const legRx = cx + (0.35) * 32;
  const legRy = cy + (0.35) * 16;
  drawIsoRect3D(g, legLx, legLy, 0.04, 0.04, 16, 0, C.METAL_GREY);
  drawIsoRect3D(g, legRx, legRy, 0.04, 0.04, 16, 0, C.METAL_GREY);

  // Cross bar
  drawIsoRect3D(g, cx, cy, 0.7, 0.04, 1, 3, C.METAL_GREY);

  // Board
  drawIsoRect3D(g, cx, cy, 0.75, 0.06, 12, 8, C.PLASTIC_WHITE);

  // Tray
  drawIsoRect3D(g, cx, cy, 0.6, 0.08, 1, 8, C.METAL_GREY);

  // Writing
  drawIsoRect3D(g, cx - 3, cy, 0.25, 0.01, 1, 16, {
    highlight: 0x5555cc, base: 0x3333aa, shadow: 0x222288, dark: 0x111166,
  });
  drawIsoRect3D(g, cx - 1, cy, 0.3, 0.01, 1, 13, {
    highlight: 0x5555cc, base: 0x3333aa, shadow: 0x222288, dark: 0x111166,
  });
  drawIsoRect3D(g, cx - 4, cy, 0.2, 0.01, 1, 11, {
    highlight: 0xcc5555, base: 0xaa3333, shadow: 0x882222, dark: 0x661111,
  });
}

/** Arcade cabinet — tall rectangular box. */
export function drawArcadeCabinet(g: Graphics, cx: number, cy: number): void {
  drawIsoRect3D(g, cx, cy, 0.4, 0.35, 26, 0, C.PLASTIC_BLACK);

  // Screen
  drawIsoRect3D(g, cx, cy, 0.32, 0.04, 10, 14, {
    highlight: 0x203060, base: 0x102040, shadow: 0x081830, dark: 0x041020,
  });

  // Pac-man + ghost
  g.circle(cx - 3, cy - 20, 2);
  g.fill({ color: 0xffff00 });
  g.circle(cx + 3, cy - 19, 1.5);
  g.fill({ color: 0xff4444 });

  // Control panel
  drawIsoRect3D(g, cx, cy, 0.35, 0.28, 2, 7, C.METAL_DARK);

  g.circle(cx - 3, cy - 9, 1.5);
  g.fill({ color: 0xcc2222 });
  g.circle(cx + 3, cy - 9, 1);
  g.fill({ color: 0x22cc22 });
  g.circle(cx + 6, cy - 10, 1);
  g.fill({ color: 0x2222cc });
}

/** Get draw function for a tile type. */
export function getFurnitureDrawFn(
  type: string,
): ((g: Graphics, cx: number, cy: number) => void) | null {
  switch (type) {
    case 'desk': return drawDesk;
    case 'coffee_machine': return drawCoffeeMachine;
    case 'plant': return drawPlantSmall;
    case 'plant_large': return drawPlantLarge;
    case 'meeting_table': return drawMeetingTable;
    case 'server_rack': return drawServerRack;
    case 'door': return drawDoor;
    case 'water_cooler': return drawWaterCooler;
    case 'vending_machine': return drawVendingMachine;
    case 'whiteboard': return drawWhiteboard;
    case 'arcade': return drawArcadeCabinet;
    default: return null;
  }
}
